// La app instalada le pregunta al servidor lo que su código no puede saber
// solo (api/app-config): si el acceso abierto sigue abierto y qué versión
// mínima se exige. En la web no hace nada: la web se reconstruye con las
// variables de Vercel y ya lo sabe.
//
// Decisión de Sebastián del 20-oct-2026: una sola actualización antes del 25.
// El 25 él cierra el acceso abierto en Vercel (paso a paso, §11.6) y la app
// instalada lo sigue por aquí, sin una segunda versión y sin quedar programada.
//
// Si la consulta falla (sin señal, servidor caído) se usa lo último que se
// supo, guardado en el teléfono; sin nada guardado, manda lo que trae el
// código (abierto). Nunca se bloquea ni se cierra por no saber.
import { isNative } from './platform';
import { fijarAccesoAbiertoRemoto } from './planGating';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LS_CLAVE = 'mipae:config-remota';
const TIMEOUT_MS = 6000;

// Lo que se acepta del servidor. Cualquier otra cosa se descarta entera.
export function normalizarConfig(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const vm = raw.version_minima && typeof raw.version_minima === 'object' ? raw.version_minima : {};
  const v = (x) => (typeof x === 'string' && /^\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(x) ? x : null);
  return {
    acceso_abierto: typeof raw.acceso_abierto === 'boolean' ? raw.acceso_abierto : null,
    version_minima: { android: v(vm.android), ios: v(vm.ios) },
  };
}

export function leerConfigGuardada() {
  try {
    const raw = localStorage.getItem(LS_CLAVE);
    return raw ? normalizarConfig(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function aplicar(config) {
  fijarAccesoAbiertoRemoto(config ? config.acceso_abierto : null);
}

// Al cargar el módulo, en la app instalada: lo último que se supo vale desde
// el primer render, antes de que responda la red.
if (isNative) aplicar(leerConfigGuardada());

// Pide la configuración y la aplica. Devuelve la vigente (la nueva con
// `fresca: true` o la guardada con `fresca: false` si la red falló), o null si
// no hay ninguna.
export async function cargarConfigRemota({ nativo = isNative, fetchImpl } = {}) {
  if (!nativo) return null;
  const pedir = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!pedir) {
    const guardada = leerConfigGuardada();
    return guardada ? { ...guardada, fresca: false } : null;
  }
  let timer;
  try {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    timer = setTimeout(() => ctrl?.abort(), TIMEOUT_MS);
    const res = await pedir(`${API_BASE}/api/app-config`, { method: 'GET', signal: ctrl?.signal });
    if (!res || !res.ok) throw new Error(`app-config ${res?.status}`);
    const config = normalizarConfig(await res.json());
    if (!config) throw new Error('app-config sin forma');
    try { localStorage.setItem(LS_CLAVE, JSON.stringify(config)); } catch { /* sin storage */ }
    aplicar(config);
    return { ...config, fresca: true };
  } catch {
    const guardada = leerConfigGuardada();
    aplicar(guardada);
    // `fresca: false`: sirve para el interruptor, nunca para el candado (sin
    // señal no se bloquea a nadie con una versión mínima vieja).
    return guardada ? { ...guardada, fresca: false } : null;
  } finally {
    clearTimeout(timer);
  }
}
